import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";

import { CatalogSectionFamily } from "../../../models/CatalogSectionFamily";
import { useSectionFamilies } from "../CatalogSectionFamilyPage";
import { useSectionOptionsFunctions } from "../hooks/useSectionFamilyOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

export const TableSectionFamily = () => {
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [sectionFamilyId, setSectionFamilyId] = useState<number | null>(null);
	const [error, setError] = useState("");

	const { data: rows, isLoading } = useSectionFamilies(undefined, (error) =>
		setError(error.message),
	);

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useSectionOptionsFunctions({
		setSectionFamilyId,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		setSectionFamilyId,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});
	const onRowDoubleClicked = (event: any) => {
		setSectionFamilyId((event.data as CatalogSectionFamily).SectionFamilyId);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "sectionFamilyContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	const onModalHide = (shouldUpdate: boolean) => {
		if (shouldUpdate) gridApi?.paginationGoToLastPage();
		setShowModal("");
	};

	useEffect(() => {
		registerConfig({
			id: "sectionFamilyContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Typography variant="h5" fontWeight={600}>
					Section Families
				</Typography>
			</Grid2>
			<Grid2
				container
				justifyContent="flex-start"
				alignItems="center"
				sx={{ width: "100%" }}
				spacing={1}
			>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
						<AgGridReact
							loading={isLoading}
							rowData={rows || []}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							animateRows
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={onRowDoubleClicked}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) =>
								String((params.data as CatalogSectionFamily).SectionFamilyId)
							}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<FooterButton
					label="SECTION FAMILY"
					showCreateModal={showModal === "create"}
					setShowCreateModal={(showCreateModal) =>
						showCreateModal ? setShowModal("create") : setShowModal("")
					}
					sizes={{ md: 3 }}
				/>
			</Grid2>
			<UpsertDeleteModal
				sectionFamilyId={sectionFamilyId}
				onHide={onModalHide}
				modal={showModal}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid2>
	);
};
